import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardCheck, Plus, Eye, Link2, Trash2, CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";
import {
  AnamnesisCreateModal,
  AnamnesisViewAnswersModal,
  publicAnamnesisLink,
} from "./AdminAnamnesisPage";

const STATUS_META = {
  pending: { label: "Aguardando", icon: Clock, cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  submitted: { label: "Respondida", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  expired: { label: "Expirada", icon: AlertTriangle, cls: "bg-muted text-muted-foreground border-border" },
};

export default function PatientAnamnesisSection({ patientId, patientName, patientEmail }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/patients/${patientId}/anamnesis`);
      setForms(data || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (token) => {
    const link = publicAnamnesisLink(token);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!");
    } catch {
      toast.error("Copie manualmente: " + link);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Excluir a ficha de "${f.patient_name || "sem nome"}"?`)) return;
    try {
      await api.delete(`/admin/anamnesis/${f.id}`);
      toast.success("Ficha excluída.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir.");
    }
  };

  const pending = forms.find((f) => f.status === "pending");
  const submitted = forms.filter((f) => f.status === "submitted");
  const latestSubmitted = submitted[0];

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="patient-anamnesis-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline text-muted-foreground mb-1 inline-flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" /> Ficha de Anamnese
          </div>
          <h3 className="font-heading font-semibold">Pré-consulta (sinais, sintomas e Bristol)</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Cruzamento automático com a tabela sintoma → vitamina/mineral.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-3 py-2 text-xs font-semibold hover:bg-primary/90 transition-colors"
          data-testid="patient-anam-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Nova ficha
        </button>
      </div>

      {/* Pending banner */}
      {pending && (
        <div
          className="mb-4 rounded-sm border-2 border-amber-500 bg-amber-500/10 p-4"
          data-testid="patient-anam-pending-banner"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-none" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-amber-900">
                Anamnese pendente de resposta
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Criada em {new Date(pending.created_at).toLocaleDateString("pt-BR")}
                {pending.expires_at && (
                  <> · Expira em {new Date(pending.expires_at).toLocaleDateString("pt-BR")}</>
                )}
              </div>
            </div>
            <button
              onClick={() => copyLink(pending.token)}
              className="inline-flex items-center gap-1 rounded-sm bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-700 transition-colors"
              data-testid="patient-anam-pending-copy"
            >
              <Link2 className="w-3.5 h-3.5" /> Copiar link
            </button>
          </div>
        </div>
      )}

      {/* Latest hypotheses preview */}
      {latestSubmitted?.hypotheses?.by_nutrient?.length > 0 && (
        <div
          className="mb-4 rounded-sm border border-emerald-500/50 bg-emerald-50/60 p-4"
          data-testid="patient-anam-hypotheses"
        >
          <div className="overline text-emerald-800 mb-1">Hipóteses de carência (última resposta)</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
            {latestSubmitted.hypotheses.by_nutrient.slice(0, 6).map((n) => (
              <li
                key={n.code}
                className="text-sm bg-card border border-emerald-500/30 rounded-sm p-2 flex items-center justify-between gap-2"
                data-testid={`patient-anam-hypo-${n.code}`}
              >
                <span className="font-medium">{n.label}</span>
                <span className="text-xs font-bold text-emerald-800 bg-emerald-100 rounded-sm px-1.5 py-0.5">
                  {n.count}
                </span>
              </li>
            ))}
          </ul>
          {latestSubmitted.hypotheses.bristol && (
            <div className="mt-3 text-xs text-emerald-900">
              <span className="font-semibold">Bristol:</span> Tipo {latestSubmitted.hypotheses.bristol.type} —{" "}
              {latestSubmitted.hypotheses.bristol.band_label}
            </div>
          )}
          <button
            onClick={() => setViewing(latestSubmitted)}
            className="mt-3 inline-flex items-center gap-1 text-xs text-primary font-semibold hover:underline"
            data-testid="patient-anam-view-details"
          >
            <Eye className="w-3.5 h-3.5" /> Ver ficha completa
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground overline">Carregando…</div>
      ) : forms.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          Nenhuma ficha vinculada. Clique em "Nova ficha" para gerar um link.
        </div>
      ) : (
        <div className="space-y-2">
          {forms.map((f) => {
            const meta = STATUS_META[f.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div
                key={f.id}
                className="flex items-start justify-between gap-3 flex-wrap p-3 rounded-sm border border-border"
                data-testid={`patient-anam-row-${f.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Criada {new Date(f.created_at).toLocaleDateString("pt-BR")}
                      {f.expires_at && <> · Expira {new Date(f.expires_at).toLocaleDateString("pt-BR")}</>}
                    </span>
                  </div>
                  {f.submitted_at && (
                    <div className="text-xs text-emerald-700 mt-1">
                      Respondida em {new Date(f.submitted_at).toLocaleString("pt-BR")}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-none">
                  {f.status === "pending" && (
                    <button
                      onClick={() => copyLink(f.token)}
                      className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`patient-anam-copy-${f.id}`}
                    >
                      <Link2 className="w-3.5 h-3.5" /> Copiar link
                    </button>
                  )}
                  {f.status === "submitted" && (
                    <button
                      onClick={() => setViewing(f)}
                      className="p-1.5 rounded-sm border border-primary text-primary hover:bg-primary/5 transition-colors inline-flex items-center gap-1 text-xs px-2"
                      data-testid={`patient-anam-view-${f.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Ver respostas
                    </button>
                  )}
                  <button
                    onClick={() => remove(f)}
                    className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                    data-testid={`patient-anam-delete-${f.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && (
        <AnamnesisCreateModal
          onClose={() => setCreating(false)}
          onCreated={() => load()}
          presetPatient={{ id: patientId, name: patientName, email: patientEmail }}
        />
      )}
      {viewing && <AnamnesisViewAnswersModal form={viewing} onClose={() => { setViewing(null); load(); }} />}
    </div>
  );
}
